import { ContextWindow } from "@asup/context-menu";
import { IEditorV3 } from "@azu-business/editor-v3";
import { AitLocation, AitTableData, useArsContext } from "aic";
import { useAppSettings, useHierarchyContext, useTlfContext } from "contexts/AppSettingsContext";
import { getClinicalLibraryData } from "contexts/hierarchy/getClinicalLibraryData";
import { getClinicalDataWithInheritance } from "contexts/tlf/getClinicalDataWithInheritance";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { isEditorPageBy, isTlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { useTransition } from "react";
import { Alert, Form } from "react-bootstrap";
import { AnnotationActionButtons } from "./AnnotationActionButtons";
import { AnnotationNavigationHeader } from "./AnnotationNavigationHeader";
import { AnnotationNotesField } from "./AnnotationNotesField";
import { AnnotationPreview } from "./AnnotationPreview";
import { AnnotationWhereGroup } from "./AnnotationWhereGroup";
import { CompanionVariableControl } from "./CompanionVariableControl";
import { getContextDisplayText } from "./getContextDisplayText";
import { AnnotationOptionType } from "./interface";
import { PrimaryVariableSelector } from "./PrimaryVariableSelector";
import { TerminologySelector } from "./TerminologySelector";
import { updateTableDataWithAnnotations } from "./updateTableDataWithAnnotations";
import { useAnnotationContextWindow } from "./useAnnotationContextWindow";
import { CompanionVariable } from "./VariableDto";

/**
 * Props for the AnnotationContextWindow component.
 */
export interface AnnotationContextWindowProps {
  isOpen: boolean;
  onClose: () => void;
  annotationType: AnnotationOptionType;
  annotationLocation?: AitLocation;
  contextAnnotation?: AnnotationWhereGroup;
  disabled?: boolean;
}

/**
 * Modal window used to create or edit annotations using where-clause logic.
 * The modal reads available clinical library data from unified state and updates
 * table data via the TLF context when saved.
 *
 * @param props - Destructured parameter object
 * @param props.isOpen - Whether the modal is visible
 * @param props.onClose - Close handler
 * @param props.annotationType - The annotation target type
 * @param props.annotationLocation - Optional table location for the annotation
 * @param props.contextAnnotation - Optional existing annotation to edit
 * @param props.disabled - Optional disabled flag
 */
export const AnnotationContextWindow = ({
  isOpen,
  onClose,
  annotationType,
  annotationLocation,
  contextAnnotation,
  disabled = false,
}: AnnotationContextWindowProps): React.ReactElement => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const tlfContext = useTlfContext();
  const { state: arsState } = useArsContext();
  const [isPending, startTransition] = useTransition();
  const clinicalLibraryData = hierarchyContext?.hierarchy
    ? getClinicalLibraryData(
        hierarchyContext.hierarchy,
        hierarchyContext.hierarchyNodes,
        appSettings.clinicalLibraries,
      )
    : undefined;

  const {
    formState,
    updateFormField,
    resetForm,
    restoreInitial,
    isFormValid,
    canAddNewClause,
    canRemoveClause,
    initialAnnotation,
    buildFinalAnnotation,
    isUnchanged,
    setDatasetAndVariable,
    currentClauseIndex,
    goToPreviousClause,
    goToNextClause,
    addNewWhereClause,
    removeCurrentClause,
    codeLists,
    comparatorTypes,
    previewText,
    totalClauseCount,
  } = useAnnotationContextWindow({
    annotationLocation,
    annotationType,
    clinicalLibrary:
      clinicalLibraryData && !appSettings.isClinicalLibraryLoading
        ? getClinicalDataWithInheritance(
            clinicalLibraryData.clinicalDatasets,
            clinicalLibraryData.clinicalCodeLists,
            clinicalLibraryData.clinicalValueLists,
            clinicalLibraryData.clinicalComments,
            clinicalLibraryData.clinicalMethods,
            clinicalLibraryData.clinicalWhereClauses,
          )
        : undefined,
    initialAnnotation: contextAnnotation,
  });

  const variables = arsState.variables;
  const isClinicalLibraryAvailable = variables.length > 0;

  /**
   * Handler for primary variable selection in "dataset.variable" format.
   * Parses the format and updates both dataset and variable fields atomically.
   *
   * @param value - Selected value in "dataset.variable" format
   */
  const handlePrimaryVariableChange = (value: string) => {
    const parts = value.split(".");
    /* istanbul ignore else -- ControlTypeAhead only calls setValue with values from availableList, which are always in "dataset.variable" format */
    if (parts.length === 2) {
      const [datasetName, variableName] = parts;
      setDatasetAndVariable(datasetName, variableName);
    }
  };

  /**
   * Get current primary variable value in "dataset.variable" format
   */
  const primaryVariableValue =
    formState.selectedDataset && formState.selectedVariable
      ? `${formState.selectedDataset}.${formState.selectedVariable.name}`
      : "";

  /**
   * Handler for companion variables changes
   *
   * @param state - State object containing companion variables array
   * @param state.companionVariables - Array of companion variables with their types and values
   */
  const handleCompanionsChange = (state: { companionVariables: CompanionVariable[] }) => {
    updateFormField("companionVariables", state.companionVariables);
  };

  const contextDisplayText = getContextDisplayText(annotationLocation);

  /**
   * Cancel handler for the annotation modal.
   * Restores initial annotation if editing, otherwise resets form to defaults.
   */
  const handleCancel = () => {
    if (initialAnnotation) {
      restoreInitial();
    } else {
      resetForm();
    }
    onClose();
  };

  /**
   * Save handler for the annotation modal.
   * Builds the final annotation and dispatches updates to the TLF context.
   * Uses startTransition to show a loader on the save button during the operation.
   */
  const handleSave = () => {
    startTransition(() => {
      const finalAnnotation = buildFinalAnnotation();

      // Handle table body annotations
      /* istanbul ignore else */
      if (tlfContext && annotationLocation && isTlfItemReadDto(tlfContext.tlfData)) {
        const body = tlfContext.tlfData.body as AitTableData<IEditorV3>;
        const updatedTableData = updateTableDataWithAnnotations(
          body,
          annotationLocation,
          finalAnnotation,
        );

        tlfContext.dispatch({
          operation: UPDATE_TLF,
          calledFrom: "AnnotationContextWindow: TableBody",
          tlfData: {
            body: updatedTableData,
          },
        });
      }
      // Handle PageBy annotation separately
      else if (
        tlfContext &&
        annotationType === "PageBy" &&
        isTlfItemReadDto(tlfContext.tlfData) &&
        isEditorPageBy(tlfContext.tlfData.pageBy)
      ) {
        tlfContext.dispatch({
          operation: UPDATE_TLF,
          calledFrom: "AnnotationContextWindow: PageBy",
          tlfData: {
            pageBy: {
              content: tlfContext.tlfData.pageBy.content,
              annotation: finalAnnotation,
            },
          },
        });
      }

      // Reset form if this was a new annotation (not editing existing)
      /* istanbul ignore else */
      if (!initialAnnotation) {
        resetForm();
      }

      onClose();
    });
  };

  return (
    <ContextWindow
      id={`annotation-modal-${annotationType}${contextDisplayText}`}
      title={`Select ${annotationType} annotation ${contextDisplayText}`}
      visible={isOpen}
      onClose={handleCancel}
      style={{
        width: "600px",
        minHeight: "500px",
        maxHeight: "80vh",
        maxWidth: "90vw",
      }}
    >
      {appSettings.isClinicalLibraryLoading ? (
        <Alert variant="info">Loading clinical library data. This may take a few seconds...</Alert>
      ) : !isClinicalLibraryAvailable ? (
        <Alert variant="warning">
          No clinical library data is available. Clinical libraries must be loaded at the hierarchy
          level to use annotations.
        </Alert>
      ) : (
        <Form style={{ padding: "16px", width: "100%" }}>
          {/* Multi-clause navigation header */}
          <AnnotationNavigationHeader
            currentClauseIndex={currentClauseIndex}
            totalClauseCount={totalClauseCount}
            canRemoveClause={canRemoveClause}
            canAddNewClause={canAddNewClause}
            disabled={disabled}
            onPrevious={goToPreviousClause}
            onNext={goToNextClause}
            onRemove={removeCurrentClause}
            onAdd={addNewWhereClause}
          />

          {/* Primary Variable Selection */}
          <PrimaryVariableSelector
            variables={variables}
            selectedValue={primaryVariableValue}
            onChange={handlePrimaryVariableChange}
            disabled={disabled}
          />

          {/* Companion Variables */}
          <CompanionVariableControl
            variables={variables}
            primaryDataset={formState.selectedDataset}
            companionVariables={formState.companionVariables}
            onChange={handleCompanionsChange}
            disabled={disabled}
          />

          {/* Terminology and Value Selection */}
          <TerminologySelector
            formState={formState}
            codeLists={codeLists}
            comparatorTypes={comparatorTypes}
            updateFormField={updateFormField}
            disabled={disabled}
          />

          <hr className="my-3" />
          <div className="mb-2">
            {/* Preview Section */}
            <AnnotationPreview previewText={previewText} />

            {/* Additional Notes */}
            <AnnotationNotesField
              value={
                formState.additionalNotes ??
                // istanbul ignore next
                ""
              }
              onChange={(value) => updateFormField("additionalNotes", value)}
              disabled={disabled}
            />
          </div>

          <AnnotationActionButtons
            isUnchanged={isUnchanged}
            isFormValid={isFormValid}
            isClinicalLibraryAvailable={isClinicalLibraryAvailable}
            disabled={disabled}
            onReset={restoreInitial}
            onCancel={handleCancel}
            onSave={handleSave}
            isPending={isPending}
          />
        </Form>
      )}
    </ContextWindow>
  );
};

AnnotationContextWindow.displayName = "AnnotationContextWindow";
